import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChild, ViewChildren, ViewEncapsulation, ChangeDetectionStrategy, inject } from '@angular/core';

import { Subscription } from 'rxjs';
import { SourcingWalletCurrency, SourcingWalletDto } from '@olv/ui-shared/models/sourcing-wallet.model';
import { WalletCardComponent } from '@olv/ui-shared/ui-shared/wallet-card/wallet-card.component';
import { FormatService } from '@olv/ui-shared/services/format.service';

interface SourcingTreeNode {
  id: string;
  name: string;
  address: string;
  networkType: number;
  risk: string;
  currencies: SourcingWalletCurrency[];
  allocatedAmount: number;
}

interface TreeEdge {
  id: string;
  path: string;
  labelX: number;
  labelY: number;
  label: string;
}

/** The real external wallet a withdrawal pays out to -- distinct from the pooled/root wallet that funds it. */
export interface SourcingTreeDestinationWallet {
  walletName: string;
  address: string;
  networkType: number;
  /** Already-converted display value (e.g. 'high', 'notassessed'), same convention as SourcingWalletDto.riskLevel. */
  riskLevel?: string;
}

@Component({
  selector: 'app-sourcing-wallet-tree',
  standalone: true,
  imports: [WalletCardComponent],
  templateUrl: './sourcing-wallet-tree.component.html',
  styleUrls: ['./sourcing-wallet-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class SourcingWalletTreeComponent implements AfterViewInit, OnChanges, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  private formatService = inject(FormatService);

  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLElement>;
  @ViewChild('destCard') destCardRef?: ElementRef<HTMLElement>;
  @ViewChild('destinationCard') destinationCardRef?: ElementRef<HTMLElement>;
  @ViewChildren('sourceCard') sourceCardRefs!: QueryList<ElementRef<HTMLElement>>;

  @Input() title = 'Estimated Sourcing Tree';
  @Input() description = 'Estimation of sourcing wallets feeding the selected source wallet.';
  @Input() sourceWallet?: SourcingWalletDto;
  @Input() sourcingWallets: SourcingWalletDto[] = [];
  @Input() showRiskLevel = true;
  /** The real external wallet the withdrawal pays out to. Omit to render the tree without this column, as before. */
  @Input() destinationWallet?: SourcingTreeDestinationWallet;

  /** No contributors means no feeder column, so the caller's description (which talks about wallets feeding the
   *  source) would describe something that is not on screen. The tree owns that column, so it owns its caption. */
  @Input() noFeedersDescription = 'The source wallet covers this withdrawal on its own; no funding wallets are involved.';


  get hasFeeders(): boolean {
    return this.sourceNodes.length > 0;
  }

  get effectiveDescription(): string {
    return this.hasFeeders ? this.description : this.noFeedersDescription;
  }

  sourceNodes: SourcingTreeNode[] = [];
  edges: TreeEdge[] = [];
  svgWidth = 0;
  svgHeight = 0;

  private viewReady = false;
  private rafId = 0;
  private resizeObserver?: ResizeObserver;
  private sourceCardRefsSub?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sourcingWallets'] || changes['sourceWallet']) {
      this.buildNodes();
      this.scheduleEdgeUpdate();
    } else if (changes['destinationWallet']) {
      // No node data to rebuild, but the new/removed destination column changes what updateEdges() must draw.
      this.scheduleEdgeUpdate();
    }
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.resizeObserver = new ResizeObserver(() => this.scheduleEdgeUpdate());
    this.resizeObserver.observe(this.canvasRef.nativeElement);
    this.sourceCardRefsSub = this.sourceCardRefs.changes.subscribe(() => this.scheduleEdgeUpdate());
    this.scheduleEdgeUpdate();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.sourceCardRefsSub?.unsubscribe();
    cancelAnimationFrame(this.rafId);
  }

  private buildNodes(): void {
    this.sourceNodes = (this.sourcingWallets ?? []).map((wallet) => ({
      id: wallet.id,
      name: wallet.walletName,
      address: wallet.address,
      networkType: wallet.networkType,
      risk: wallet.riskLevel,
      currencies: wallet.currencies,
      allocatedAmount: wallet.allocatedAmount ?? 0,
    }));
  }

  private scheduleEdgeUpdate(): void {
    if (!this.viewReady) {
      return;
    }
    cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => requestAnimationFrame(() => this.updateEdges()));
  }

  private updateEdges(): void {
    const canvas = this.canvasRef?.nativeElement;
    const dest = this.destCardRef?.nativeElement;
    if (!canvas || !dest) {
      this.edges = [];
      this.cd.detectChanges();
      return;
    }

    const canvasRect = canvas.getBoundingClientRect();
    this.svgWidth = canvas.clientWidth;
    this.svgHeight = canvas.clientHeight;

    // Vertical alignment of all three columns is pure CSS now (align-items: center on .sourcing-tree-canvas) --
    // no JS-computed transform. That used to re-measure card positions via getBoundingClientRect() on every
    // scheduleEdgeUpdate() call, including the ~32ms (double rAF) after a card's own 350ms CSS entrance animation
    // starts -- i.e. routinely BEFORE that animation had settled, since transforms affect what
    // getBoundingClientRect() reports. A transient, animation-timing-dependent offset computed from that and then
    // applied as this column's OWN transform was a plausible source of the intermittent overlap: cards ending up
    // vertically offset from where the (also freshly re-measured) edges assumed they'd land.
    const destRect = dest.getBoundingClientRect();

    // The template renders exactly one #sourceCard per sourceNodes entry, in order and unfiltered, so nodes and
    // card refs line up by index. Zipping them keeps the node's identity typed rather than round-tripping it
    // through a data- attribute; the only possible skew is a transient length mismatch while the DOM refs catch
    // up with the data, which the cards[i] guard below covers.
    const cards = this.sourceCardRefs.toArray();

    const feederEdges = this.sourceNodes
      .map((node, i): TreeEdge | null => {
        const el = cards[i]?.nativeElement;
        if (!el) return null;

        const rect = el.getBoundingClientRect();
        // formatCoinCurrency defaults precision to 0, so an empty currencies array needs no extra fallback here.
        const label = this.formatService.formatCoinCurrency(node.allocatedAmount, node.currencies[0]?.displayPrecision);
        return this.buildEdge(node.id, rect, destRect, canvasRect, label);
      })
      .filter((edge): edge is TreeEdge => edge !== null);

    // Second hop: root pooled wallet -> the real external destination, labeled with the withdrawal's own total
    // (already what sourceWallet.currencies[0].amount holds -- see GetSourcingWalletsAsync's totalRequired).
    const destinationEl = this.destinationCardRef?.nativeElement;
    const destinationEdge =
      destinationEl && this.destinationWallet
        ? this.buildEdge(
            'destination',
            destRect,
            destinationEl.getBoundingClientRect(),
            canvasRect,
            this.formatService.formatCoinCurrency(
              this.sourceWallet?.currencies?.[0]?.amount,
              this.sourceWallet?.currencies?.[0]?.displayPrecision,
            ),
          )
        : null;

    this.edges = destinationEdge ? [...feederEdges, destinationEdge] : feederEdges;

    this.cd.detectChanges();
  }

  /** Builds one straight, right-angled connector (canvas-relative coordinates) from the right edge of `fromRect` to
   *  the left edge of `toRect`, plus its label position -- shared by the feeder->root and root->destination edges.
   *  A short horizontal stub leaves the source, a vertical run sits at a corner positioned a fixed offset before the
   *  destination, then a short horizontal run enters it. Every edge sharing the same destination lands on the same
   *  cornerX (it depends only on `toRect`), so their vertical runs align into what reads as one shared trunk line
   *  without any edge needing to know about its siblings. */
  private buildEdge(id: string, fromRect: DOMRect, toRect: DOMRect, canvasRect: DOMRect, label: string): TreeEdge {
    const startX = fromRect.right - canvasRect.left;
    const startY = fromRect.top - canvasRect.top + fromRect.height / 2;
    const endX = toRect.left - canvasRect.left - 6;
    const endY = toRect.top - canvasRect.top + toRect.height / 2;

    const cornerX = Math.max(startX + 12, endX - 24);
    const path = `M ${startX} ${startY} L ${cornerX} ${startY} L ${cornerX} ${endY} L ${endX} ${endY}`;

    // Label sits at the true midpoint of the gap between the two cards (startX to endX), NOT tied to cornerX.
    // cornerX is clamped for narrow gaps (Math.max above) and can land close to startX, and a label anchored to it
    // (the previous approach) rode along with that clamp -- pulling the label backward toward, and overlapping,
    // the source card whenever the gap was tight. The true midpoint stays centered in the actual visual gap
    // regardless of where the corner bend is, so it's the only anchor that can't drift into either card.
    return { id, path, labelX: (startX + endX) / 2, labelY: startY, label };
  }
}
